/**
 * Squad agent governance client — the read/resolve surface behind the agent
 * detail "Approvals" and "Activity" tabs. Talks to the same Squad backend as
 * {@link squadAgent} and reuses its `squadFetch` (Bearer JWT + `saas-app-token`
 * header, single 401-refresh retry).
 *
 *   - Approvals: `GET /api/approvals/` (read-only queue) + `POST /api/chat/resume/`
 *     to accept/reject a pending request.
 *   - Activity:  `GET /api/runs/?agent=<id>` (the agent's run history).
 */

import { browserTimezone } from 'src/utils/browserTimezone'

import { jsonOrThrow, squadFetch, SquadRequestError } from './squadAgent'

/* ------------------------------------------------------------------ *
 * Approvals
 * ------------------------------------------------------------------ */

/** Lifecycle of a human-in-the-loop approval (Squad `Approval.Status`). */
export type SquadApprovalStatus =
  | 'pending'
  | 'accepted'
  | 'edited'
  | 'rejected'
  | 'answered'

export type SquadApproval = {
  id: string
  /** Squad agent id the paused tool call belongs to. */
  agent: string
  thread: string
  run: string
  /** Tool whose call is awaiting a decision, e.g. `send_email`. */
  tool_name: string
  /** The tool's input arguments. */
  payload: Record<string, unknown>
  status: SquadApprovalStatus
  /** Filled once resolved: `{ action, overrides?, reason? }`. */
  decision: Record<string, unknown> | null
  created_at: string
  resolved_at: string | null
  resolved_by_sso_id: string
}

type Paginated<T> = { count?: number; results?: T[] }

const unwrap = <T>(data: T[] | Paginated<T>): T[] =>
  Array.isArray(data) ? data : (data.results ?? [])

/**
 * Approvals, newest first. Pass `agentId` to filter to one agent server-side
 * (`?agent=`); omit it to list every approval in the workspace (the endpoint is
 * already account- + visibility-scoped). Supports the tab's agent/workspace
 * toggle. Also accepts `?status=`, applied client-side by the tab.
 */
export const listApprovals = async (
  appToken: string,
  agentId?: string
): Promise<SquadApproval[]> => {
  const params = new URLSearchParams({ page_size: '100' })
  if (agentId) params.set('agent', agentId)
  const response = await squadFetch(
    `/api/approvals/?${params.toString()}`,
    { method: 'GET' },
    appToken
  )
  const data = await jsonOrThrow<SquadApproval[] | Paginated<SquadApproval>>(
    response
  )

  return unwrap(data).sort((a, b) => b.created_at.localeCompare(a.created_at))
}

/**
 * Workspace-wide count of pending approvals (across all agents the caller can
 * see). Reads the paginated envelope's `count` rather than the rows — one cheap
 * request, used for the agents-list KPI strip.
 */
export const countWorkspacePendingApprovals = async (
  appToken: string
): Promise<number> => {
  const params = new URLSearchParams({ status: 'pending', page_size: '1' })
  const response = await squadFetch(
    `/api/approvals/?${params.toString()}`,
    { method: 'GET' },
    appToken
  )
  const data = await jsonOrThrow<SquadApproval[] | Paginated<SquadApproval>>(
    response
  )

  if (Array.isArray(data)) return data.length
  return data.count ?? data.results?.length ?? 0
}

/** Decision sent to resolve a pending approval. */
export type ApprovalAction = 'accept' | 'edit' | 'reject'

/**
 * Resolve a pending approval via `POST /api/chat/resume/`.
 *
 * The endpoint flips the approval's status synchronously (before it streams)
 * and hands the resumed run to a Celery worker that outlives the SSE socket, so
 * we only need the POST to land — we cancel the event stream immediately rather
 * than draining the agent's whole continuation.
 */
export const resolveApproval = async (
  appToken: string,
  approvalId: string,
  action: ApprovalAction,
  opts?: { overrides?: Record<string, unknown>; reason?: string }
): Promise<void> => {
  const response = await squadFetch(
    '/api/chat/resume/',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify({
        approval_id: approvalId,
        action,
        // The resumed run keeps working after this call; give it the
        // decider's wall clock so `current_time` isn't the server's UTC.
        timezone: browserTimezone(),
        ...(opts?.overrides ? { overrides: opts.overrides } : {}),
        ...(opts?.reason ? { reason: opts.reason } : {})
      })
    },
    appToken
  )

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }

  // Resolution has already landed server-side; the body just tails the resumed
  // run, which we don't render here.
  await response.body?.cancel().catch(() => undefined)
}

/* ------------------------------------------------------------------ *
 * Activity (runs)
 * ------------------------------------------------------------------ */

export type SquadRunStatus = 'pending' | 'running' | 'done' | 'error' | 'paused'

export type SquadRunTrigger = 'chat' | 'schedule' | 'webhook' | 'api'

export type SquadRun = {
  id: string
  thread: string
  agent: string
  agent_name: string
  schedule: string | null
  status: SquadRunStatus
  trigger: SquadRunTrigger
  input: Record<string, unknown> | null
  output: Record<string, unknown> | null
  error: string
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  created_by_sso_id: string
  started_at: string | null
  finished_at: string | null
  created_at: string
}

/** The agent's run history (chat/scheduled/webhook/api), newest first. */
export const listAgentRuns = async (
  appToken: string,
  agentId: string,
  pageSize = 50
): Promise<SquadRun[]> => {
  const params = new URLSearchParams({
    agent: agentId,
    page_size: String(pageSize)
  })
  const response = await squadFetch(
    `/api/runs/?${params.toString()}`,
    { method: 'GET' },
    appToken
  )
  const data = await jsonOrThrow<SquadRun[] | Paginated<SquadRun>>(response)

  return unwrap(data).sort((a, b) => b.created_at.localeCompare(a.created_at))
}
